import MapScale from '@/Enums/Map/MapScale.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import type { IGamTotalsRecord } from '@/Interfaces/ReportingDashboard/Gam/ReportRecord.interface';
import type { IGamState } from '@/Interfaces/Slices/ReportingDashboard/Gam/State.interface';
import GamReportView, { type GamGridView } from '@/types/ReportingDashboard/Gam.type';
import { DEFAULT_LOADING_REQUEST_ID } from '../slice';

export const defaultGamTotals: IGamTotalsRecord = {
  // Null until a snapshot resolves — avoids showing "0" while prefetch runs.
  impressions: null,
  clicks: null,
  reach: null,
  frequency: null,
  videoCompletionRate: null,
};

export const initialGamState: IGamState = {
  campaignOptions: [],
  // Not the loading sentinel: a filter source that never resolves must leave the
  // control usable-but-empty, never permanently greyed out — the exact failure
  // #4235 is about.
  campaignOptionsRequestId: null,
  totals: defaultGamTotals,
  dateReport: [],
  deviceReport: [],
  regionReport: [],
  channelReport: [],
  advertiserReport: [],
  campaignReport: [],
  placementReport: [],
  creativeReport: [],
  publisherReport: [],
  daypartReport: [],
  dayOfWeekReport: [],
  dealsReport: [],
  geoFenceReport: [],

  dmaReport: [],
  countyReport: [],
  cityReport: [],
  zipReport: [],
  congressionalDistrictReport: [],

  totalsRequestId: DEFAULT_LOADING_REQUEST_ID,
  totalsReach: null,
  totalsReachRequestId: DEFAULT_LOADING_REQUEST_ID,
  totalsReachTimedOut: false,
  dateReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  deviceReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  regionReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  channelReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  advertiserReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaignReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  placementReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  creativeReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  publisherReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  daypartReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dayOfWeekReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dealsReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  geoFenceReportRequestId: DEFAULT_LOADING_REQUEST_ID,

  dmaReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  countyReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  cityReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  zipReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  congressionalDistrictReportRequestId: DEFAULT_LOADING_REQUEST_ID,

  stateBoundariesData: null,
  stateBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaBoundariesData: null,
  dmaBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  countyBoundariesData: null,
  countyBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  cityBoundariesData: null,
  cityBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  zipBoundariesData: null,
  zipBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  congressionalDistrictBoundariesData: null,
  congressionalDistrictBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  // Default to State so the initial render mirrors Phase 1 behaviour.
  mapScale: MapScale.State,

  // GAM has no attribution surface — toggle defaults to Delivery.
  viewType: ViewType.Delivery,

  errorCardMessage: '',
  dataIsRefreshing: false,
};

// Order matches simplifiReportViews (Simplifi tab) so the user sees the
// same set of radios in the same order across tabs. Region/Channel order
// follows Simplifi (Channel sits between Date and Device).
export const gamReportViews: GamReportView[] = [
  GamReportView.Date,
  GamReportView.Channel,
  GamReportView.Device,
  GamReportView.Advertiser,
  GamReportView.Campaign,
  GamReportView.Placement,
  GamReportView.Creative,
  GamReportView.Publisher,
  GamReportView.DayOfWeek,
  GamReportView.Daypart,
  // Deals + GeoFence intentionally omitted — GAM has no analog for either,
  // so the backend always returns an empty grid (Simplifi-parity stubs).
  // Per Alex 2026-06-02, drop the always-empty tabs from Impressions-by-Type.
];

// Geo map scales are also printable from the grid via the "Geo" submenu,
// mirroring Simplifi.
export const gamMapScales: MapScale[] = [
  MapScale.State,
  MapScale.Dma,
  MapScale.County,
  MapScale.Zip,
  MapScale.CongressionalDistricts,
  MapScale.City,
];

export const allowedGamTablesToPrint: GamGridView[] = [...gamReportViews, ...gamMapScales];
